import {
  DEFAULT_COUNTRY,
  DEFAULT_LANGUAGE,
  COUNTRY_LANGUAGES,
  formatLocale,
  LOCALE_FORMAT,
  SUPPORTED_COUNTRIES,
} from '@src/config/localisation/locale-config.constants'
import type { TAbsoluteLocale, TCountry, TRegionDetail } from '@src/config/localisation/locale-config.types'
import { PAGE_TYPE_TO_PATH_PREFIX } from '@src/constants/page-type'
import type { ClpMigrationMap } from '@src/server-side/api/fetch-clp-migration-slugs'
import { parseFormat } from '@src/utils/general.utils'
import { parseLocale, allUrlLocales } from '@src/utils/i18n.utils'

type RegionInfo = NonNullable<TRegionDetail['regions'][TCountry]>
interface RegionAliases {
  country?: Record<string, string>
}

// Use case: RMS may expose user-facing country aliases (e.g. uk -> gb).
// Normalize alias inputs to canonical countries before locale/URL decisions.
const getCanonicalCountryFromAlias = (country: string, regionDetails: TRegionDetail): TCountry => {
  const normalizedCountry = country.toLowerCase()
  const regionAliases = regionDetails.regionAliases as RegionAliases | undefined
  const countryAliases: Record<string, string> = regionAliases?.country ?? {}
  const countryAlias = countryAliases[normalizedCountry]?.toLowerCase()
  if (countryAlias && regionDetails.regions[countryAlias as TCountry]) return countryAlias as TCountry

  if (regionDetails.regions[normalizedCountry as TCountry]) return normalizedCountry as TCountry

  return normalizedCountry as TCountry
}

const normalizeLocale = (locale: string): TAbsoluteLocale => {
  return locale.toLowerCase() as TAbsoluteLocale
}

// Use case: callers can provide either country, alias country, or full locale.
// Normalize once to keep switch/compare logic consistent across entry points.
const normalizeRegionInput = (input: string, regionDetails: TRegionDetail): TAbsoluteLocale | TCountry => {
  const normalizedInput = input.toLowerCase()
  if (normalizedInput.includes('-')) {
    return normalizeLocale(normalizedInput)
  }
  return getCanonicalCountryFromAlias(normalizedInput, regionDetails)
}

export const getFullLocaleForRegion = (target: TAbsoluteLocale | TCountry, regionDetails: TRegionDetail): string => {
  const normalizedTarget = normalizeRegionInput(String(target), regionDetails)
  let targetLocale = normalizedTarget
  const region = (regionDetails.regions[targetLocale as TCountry]?.regionCode ??
    parseFormat(LOCALE_FORMAT, targetLocale as TAbsoluteLocale)?.country ??
    '') as TCountry

  //if the confirmation locale is the same as the region, set the confirmation locale to the default language iso code
  if (targetLocale === region) {
    targetLocale = regionDetails.regions[region]?.defaultLanguageIsoCode as TAbsoluteLocale
  }
  return targetLocale
}

export const checkRegionChanged = ({
  locale,
  regionDetails,
}: {
  locale: TAbsoluteLocale
  regionDetails: TRegionDetail
}): {
  isRegionChange: boolean
  targetLocale: TAbsoluteLocale | TCountry | ''
} => {
  // Use case: show the heads-up modal only when URL region and active locale country differ,
  // including alias/display-code URL forms like /uk.
  const { country: currentCountry } = parseLocale(locale)

  if (typeof window === 'undefined') {
    return { isRegionChange: false, targetLocale: '' }
  }

  try {
    // During client-side navigation, window.location might be temporarily invalid
    if (!window.location.origin || !window.location.pathname) {
      return { isRegionChange: false, targetLocale: '' }
    }
    const currentURL = new URL(window.location.pathname, window.location.origin)
    const pathnameParts = currentURL.pathname.split('/')
    const regionInfoFromURL = pathnameParts[1]?.toLowerCase() ?? ''
    const normalizedRegionInfoFromURL = regionInfoFromURL ? normalizeRegionInput(regionInfoFromURL, regionDetails) : ''

    const isValidURLRegionInfo =
      Boolean(regionDetails.regions[normalizedRegionInfoFromURL as TCountry]) ||
      (Object.values(regionDetails.regions) as (RegionInfo | undefined)[]).some(region =>
        region ? Object.hasOwn(region.supportedLanguages, normalizedRegionInfoFromURL as TAbsoluteLocale) : false,
      )
    //target region info can either be a country or a language, targetLocale is the full locale with the language and country, default if regionInfoFromURL is only country
    const targetRegionInfo = isValidURLRegionInfo ? String(normalizedRegionInfoFromURL) : ''
    const targetLocale = getFullLocaleForRegion(targetRegionInfo as TAbsoluteLocale | TCountry, regionDetails)

    if (targetLocale) {
      const isRegionChange = parseLocale(targetLocale as TAbsoluteLocale).country !== currentCountry
      return {
        isRegionChange,
        targetLocale: targetLocale as TAbsoluteLocale,
      }
    }
    return {
      isRegionChange: false,
      targetLocale: '',
    }
  } catch (error) {
    // If URL construction fails during navigation, return safe defaults
    return { isRegionChange: false, targetLocale: '' }
  }
}

export const getUpdatedLocaleForURLAfterRegionSwitch = (
  targetLocale: TAbsoluteLocale | TCountry,
  regionDetails: TRegionDetail,
): string => {
  // Use case: when switching country/language, derive URL prefix from RMS display code
  // for default locales (e.g. en-gb -> /uk) while preserving existing US/CA behavior.
  const normalizedTargetLocale = normalizeRegionInput(String(targetLocale), regionDetails)
  // Handle default locale - no prefix needed
  if (normalizedTargetLocale === DEFAULT_COUNTRY || normalizedTargetLocale === DEFAULT_LANGUAGE) {
    return ''
  }

  let localeInfo: RegionInfo | undefined
  //Get locale info from region details
  if (SUPPORTED_COUNTRIES.includes(normalizedTargetLocale as TCountry)) {
    localeInfo = regionDetails.regions[normalizedTargetLocale as TCountry]
  } else if (String(normalizedTargetLocale).length > 0) {
    const parsedLocale = parseFormat(LOCALE_FORMAT, normalizedTargetLocale as TAbsoluteLocale) as {
      country?: string
    } | null
    const localeCountry = parsedLocale?.country?.toLowerCase() as TCountry | undefined
    localeInfo = localeCountry ? regionDetails.regions[localeCountry] : undefined
  }

  const regionUrlCode = localeInfo ? String(localeInfo.regionDisplayCode ?? localeInfo.regionCode).toLowerCase() : ''
  //if the locale is the same as the region or the default language iso code, return country
  if (
    normalizedTargetLocale === localeInfo?.regionCode ||
    normalizedTargetLocale === localeInfo?.defaultLanguageIsoCode
  ) {
    return regionUrlCode
  } else {
    return normalizedTargetLocale
  }
}

// Use case: locale-prefix absolute URLs emitted into JSON-LD schema (offers,
// breadcrumbs) so crawlers see the same per-region URL the user would land on
// after a region switch. Idempotent — calling twice doesn't double-prefix.
export const prefixSchemaUrlWithLocale = (
  url: string,
  locale: TAbsoluteLocale | undefined,
  regionDetails: TRegionDetail,
): string => {
  if (!url || !locale) return url
  try {
    const prefix = getUpdatedLocaleForURLAfterRegionSwitch(locale, regionDetails)
    if (!prefix) return url
    const parsed = new URL(url)
    const firstSegment = parsed.pathname.split('/').filter(Boolean)[0]?.toLowerCase()
    if (firstSegment === prefix) return url
    parsed.pathname = `/${prefix}${parsed.pathname}`
    return parsed.toString()
  } catch {
    return url
  }
}

const normalizeToFullLocale = (value: string): string => {
  const v = value.toLowerCase()
  if (SUPPORTED_COUNTRIES.includes(v as TCountry)) {
    const defaultLanguageForCountry = COUNTRY_LANGUAGES[v as TCountry][0]
    if (defaultLanguageForCountry) {
      return formatLocale(v as TCountry, defaultLanguageForCountry).toLowerCase()
    }
  }
  return v
}

export const isQueryParamsLocaleSwitch = (origin: string, destination: string): boolean => {
  // Use case: track heads-up modal telemetry only for recognized URL locales/countries.
  const originNorm = origin.toLowerCase()
  const destNorm = normalizeToFullLocale(destination)
  return (
    originNorm !== destNorm &&
    allUrlLocales.includes(originNorm as TAbsoluteLocale) &&
    allUrlLocales.includes(destNorm as TAbsoluteLocale)
  )
}

const SHOP_PREFIX = `/${PAGE_TYPE_TO_PATH_PREFIX.COLLECTION}/`

export type ClpMigrationAction = 'noop' | 'kept-shop' | 'stripped-shop'

export interface ClpMigrationResolution {
  path: string
  action: ClpMigrationAction
}

/**
 * Pure resolver for region-switch URL rewriting on `/shop/{slug}` pages.
 *
 * Three cases on switch A → B from `/shop/{slug}`:
 * 1. `/shop/{slug}` exists in target region B → keep `/shop/{slug}` (canonical).
 * 2. Only exists in current region A → strip `/shop/`, use A's `targetSlug` in B.
 * 3. Neither has it → leave unchanged; accept natural course of navigation - 404 too (CMS-authored or unknown).
 *
 * Pre-narrowed maps from SSR contain at most one entry per region (the current
 * pathname's), so lookup is `map[basePath]` without iteration.
 */
export const resolveClpMigrationPath = ({
  pathname,
  currentRegionId,
  targetRegionId,
  maps,
}: {
  pathname: string
  currentRegionId: number | undefined
  targetRegionId: number | undefined
  maps: Record<number, ClpMigrationMap> | undefined
}): ClpMigrationResolution => {
  if (!maps || !currentRegionId || !targetRegionId || currentRegionId === targetRegionId) {
    return { path: pathname, action: 'noop' }
  }
  const [path, query = ''] = pathname.split('?')
  if (!path?.startsWith(SHOP_PREFIX)) return { path: pathname, action: 'noop' }

  const targetMap = maps[targetRegionId]
  if (targetMap?.[path]) return { path: pathname, action: 'kept-shop' }

  const aliasInCurrent = maps[currentRegionId]?.[path]
  if (aliasInCurrent?.slug) {
    const stripped = aliasInCurrent.slug.startsWith('/') ? aliasInCurrent.slug : `/${aliasInCurrent.slug}`
    return { path: stripped + (query ? `?${query}` : ''), action: 'stripped-shop' }
  }

  return { path: pathname, action: 'noop' }
}
